import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Trash2, Tv, Settings2, X, Eye } from "lucide-react";

const DEFAULT_PRESET = {
  view: "globe",
  auto_rotate: true,
  show_cve_ticker: true,
  show_hotspot_panel: true,
  severity_filter: { low: true, medium: true, high: true, critical: true },
  pinned_hotspots: [],
  title_override: null,
};

// Presence window — walls that heartbeat within this many seconds show as ONLINE.
const PRESENCE_ONLINE_SEC = 60;
const PRESENCE_IDLE_SEC = 5 * 60;

function PresenceLED({ lastUsedAt, useCount, revoked }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  if (revoked) {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="presence-revoked">
        <span className="w-2 h-2 rounded-full bg-[#3f3f46]" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">Revoked</span>
      </span>
    );
  }
  if (!lastUsedAt) {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="presence-never">
        <span className="w-2 h-2 rounded-full bg-[#52525B]" />
        <span className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">
          {useCount ? "Idle" : "Never seen"}
        </span>
      </span>
    );
  }
  const ageSec = Math.max(0, (Date.now() - new Date(lastUsedAt).getTime()) / 1000);
  let color = "#8E9196", label = "OFFLINE", glow = null, testid = "presence-offline";
  if (ageSec <= PRESENCE_ONLINE_SEC) {
    color = "#00F0FF"; label = "ONLINE"; glow = "0 0 8px #00F0FF"; testid = "presence-online";
  } else if (ageSec <= PRESENCE_IDLE_SEC) {
    color = "#FFC542"; label = "IDLE"; testid = "presence-idle";
  }
  const relTime =
    ageSec < 60 ? `${Math.floor(ageSec)}s ago` :
    ageSec < 3600 ? `${Math.floor(ageSec / 60)}m ago` :
    ageSec < 86400 ? `${Math.floor(ageSec / 3600)}h ago` :
    `${Math.floor(ageSec / 86400)}d ago`;
  return (
    <span className="inline-flex items-center gap-1.5" title={`Last heartbeat ${relTime}`} data-testid={testid}>
      <span
        className={`w-2 h-2 rounded-full ${label === "ONLINE" ? "animate-pulse" : ""}`}
        style={{ background: color, boxShadow: glow || undefined }}
      />
      <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color }}>{label}</span>
      <span className="text-[9px] font-mono text-[#52525B]">· {relTime}</span>
    </span>
  );
}

export default function AdminKioskTokens() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [ttlDays, setTtlDays] = useState(30);
  const [scopes, setScopes] = useState({ threat_map: true, shift_briefing: false });
  const [createPreset, setCreatePreset] = useState(DEFAULT_PRESET);
  const [showRaw, setShowRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // token doc when editing preset

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/kiosk/tokens");
      setTokens(r.data.tokens || []);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    // Auto-refresh so presence LEDs update as walls heartbeat.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  async function create() {
    if (!name.trim()) { toast.error("Give this kiosk a name"); return; }
    const scopeList = Object.entries(scopes).filter(([, v]) => v).map(([k]) => k);
    if (!scopeList.length) { toast.error("Pick at least one scope"); return; }
    try {
      const r = await api.post("/admin/kiosk/tokens", {
        name: name.trim(),
        ttl_days: ttlDays,
        preset: createPreset,
        scopes: scopeList,
      });
      // Choose viewer URL based on scopes granted.
      const path = scopeList.includes("threat_map") ? "/kiosk/threat-map" : "/kiosk/briefing";
      const url = `${window.location.origin}${path}?token=${r.data.token}`;
      setShowRaw({ name: r.data.name, url, token: r.data.token });
      toast.success(`Kiosk URL created for ${r.data.name}`);
      setName("");
      setCreatePreset(DEFAULT_PRESET);
      setScopes({ threat_map: true, shift_briefing: false });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create token");
    }
  }

  async function previewToken(t) {
    try {
      const r = await api.post(`/admin/kiosk/tokens/${t.id}/preview`);
      const path = (t.scopes || ["threat_map"]).includes("threat_map") ? "/kiosk/threat-map" : "/kiosk/briefing";
      const url = `${window.location.origin}${path}?token=${r.data.token}`;
      window.open(url, "_blank", "noopener");
      toast.success(`Preview URL opened — expires in ${Math.round(r.data.expires_in_seconds / 60)} min`);
    } catch (e) {
      toast.error("Preview failed");
    }
  }

  async function savePreset(tokenId, preset) {
    try {
      await api.patch(`/admin/kiosk/tokens/${tokenId}/preset`, { preset });
      toast.success("Preset saved — screens refresh on next reconnect");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Failed to save preset");
    }
  }

  async function revoke(id, tokName) {
    if (!window.confirm(`Revoke kiosk token for ${tokName}? Any screens using it will disconnect immediately.`)) return;
    try {
      await api.delete(`/admin/kiosk/tokens/${id}`);
      toast.success("Token revoked");
      await load();
    } catch (e) {
      toast.error("Revoke failed");
    }
  }

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="admin-kiosk-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Tv className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
          <h1 className="font-display font-black text-3xl tracking-tight">KIOSK URLS</h1>
        </div>
        <p className="text-sm text-[#8E9196] max-w-2xl mb-6">
          Share-a-link URLs for your SOC wall monitors. Read-only Threat Map + CVE ticker with no login required.
          Revoke anytime — screens disconnect the moment a token is invalidated.
        </p>

        {/* Create form */}
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5 mb-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3">
            New kiosk URL
          </div>
          <div className="flex gap-3">
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. NOC Wall A · Meeting Room · Reception TV"
              data-testid="kiosk-name-input"
              className="flex-1 bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white placeholder:text-[#52525B]"
            />
            <select
              value={ttlDays} onChange={(e) => setTtlDays(Number(e.target.value))}
              className="bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white"
              data-testid="kiosk-ttl-select"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={365}>1 year</option>
            </select>
            <button
              onClick={create}
              data-testid="kiosk-create-btn"
              className="flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-black font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-white transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Create URL
            </button>
          </div>
          <PresetEditor
            preset={createPreset}
            onChange={setCreatePreset}
            data-testid="kiosk-create-preset"
          />
          <div className="mt-3 flex gap-4 pt-3 border-t border-white/5" data-testid="kiosk-scope-picker">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Scopes:</span>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={scopes.threat_map}
                     onChange={(e) => setScopes({ ...scopes, threat_map: e.target.checked })}
                     className="accent-[#00F0FF]"
                     data-testid="scope-threat-map" />
              <span className="uppercase text-[10px] font-mono tracking-widest text-[#8E9196]">Threat Map</span>
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={scopes.shift_briefing}
                     onChange={(e) => setScopes({ ...scopes, shift_briefing: e.target.checked })}
                     className="accent-[#B57BFF]"
                     data-testid="scope-shift-briefing" />
              <span className="uppercase text-[10px] font-mono tracking-widest text-[#8E9196]">Shift Briefing (public digest)</span>
            </label>
          </div>
        </div>

        {/* Raw URL modal */}
        {showRaw && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={() => setShowRaw(null)}>
            <div className="bg-[#0a0a0a] border border-[#00F0FF]/60 rounded-sm p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC542] mb-2">
                ⚠ Shown once — copy now
              </div>
              <div className="font-display font-black text-2xl mb-3">
                Kiosk URL · {showRaw.name}
              </div>
              <p className="text-xs text-[#8E9196] font-mono mb-4">
                Paste this URL into any browser on the wall PC. No login required.
              </p>
              <div className="flex gap-2 mb-4">
                <input
                  readOnly value={showRaw.url}
                  className="flex-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-xs font-mono text-white"
                  data-testid="kiosk-created-url"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(showRaw.url); toast.success("Copied to clipboard"); }}
                  data-testid="kiosk-copy-btn"
                  className="flex items-center gap-2 px-3 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowRaw(null)}
                  className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Existing tokens */}
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a]" data-testid="kiosk-token-list">
          <div className="grid grid-cols-[1fr_130px_120px_130px_100px_60px] px-4 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            <div>Name</div>
            <div>Presence</div>
            <div>Created</div>
            <div>Expires</div>
            <div>Uses</div>
            <div></div>
          </div>
          {loading && <div className="p-6 text-xs text-[#52525B] font-mono">Loading…</div>}
          {!loading && tokens.length === 0 && (
            <div className="p-6 text-xs text-[#52525B] font-mono">No kiosk URLs yet.</div>
          )}
          {tokens.map((t) => (
            <div key={t.id}
                 className={`grid grid-cols-[1fr_130px_120px_130px_100px_60px] items-center px-4 py-3 border-b border-white/[0.04] last:border-0 text-sm ${
                   t.revoked ? "opacity-40" : ""
                 }`}>
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-[10px] font-mono text-[#8E9196]">by {t.created_by || "system"}</div>
              </div>
              <div data-testid={`kiosk-presence-${t.id}`}>
                <PresenceLED lastUsedAt={t.last_used_at} useCount={t.use_count} revoked={t.revoked} />
              </div>
              <div className="text-[10px] font-mono text-[#8E9196]">
                {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="text-[10px] font-mono text-[#8E9196]">
                {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : "—"}
              </div>
              <div className="text-[10px] font-mono text-[#8E9196]">
                {t.use_count || 0}
              </div>
              <div className="text-right flex items-center justify-end gap-1">
                {t.revoked ? (
                  <span className="text-[10px] font-mono text-[#FF2E63]">REVOKED</span>
                ) : (
                  <>
                    <button
                      onClick={() => previewToken(t)}
                      className="p-1.5 border border-white/10 hover:border-[#FFC542] hover:text-[#FFC542] rounded-sm text-[#8E9196] transition-colors"
                      title="Preview this wall (opens a 10-min token in a new tab)"
                      data-testid={`kiosk-preview-${t.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setEditing(t)}
                      className="p-1.5 border border-white/10 hover:border-[#B57BFF] hover:text-[#B57BFF] rounded-sm text-[#8E9196] transition-colors"
                      title="Edit preset"
                      data-testid={`kiosk-preset-${t.id}`}
                    >
                      <Settings2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => revoke(t.id, t.name)}
                      className="p-1.5 border border-white/10 hover:border-[#FF2E63] hover:text-[#FF2E63] rounded-sm text-[#8E9196] transition-colors"
                      title="Revoke"
                      data-testid={`kiosk-revoke-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preset edit modal */}
      {editing && (
        <PresetModal
          token={editing}
          onCancel={() => setEditing(null)}
          onSave={(p) => savePreset(editing.id, p)}
        />
      )}
    </div>
  );
}

function PresetEditor({ preset, onChange }) {
  const set = (patch) => onChange({ ...preset, ...patch });
  const setSev = (k, v) =>
    onChange({ ...preset, severity_filter: { ...preset.severity_filter, [k]: v } });

  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid="kiosk-preset-editor">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-3">
        Preset for this wall screen
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">View</div>
          <div className="flex gap-1">
            {["globe", "map"].map((v) => (
              <button
                key={v}
                onClick={() => set({ view: v })}
                data-testid={`preset-view-${v}`}
                className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors ${
                  preset.view === v ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "text-[#8E9196] border-white/10 hover:text-white"
                }`}
              >{v}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">Wall title override</div>
          <input
            value={preset.title_override || ""}
            onChange={(e) => set({ title_override: e.target.value || null })}
            placeholder="e.g. NOC Wall · Room 42"
            data-testid="preset-title-input"
            className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-2 py-1 text-xs outline-none text-white placeholder:text-[#52525B]"
          />
        </div>
        <div className="col-span-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">Severity filter</div>
          <div className="flex gap-2 flex-wrap">
            {["low", "medium", "high", "critical"].map((k) => (
              <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!preset.severity_filter?.[k]}
                  onChange={(e) => setSev(k, e.target.checked)}
                  className="accent-[#00F0FF]"
                  data-testid={`preset-sev-${k}`}
                />
                <span className="uppercase text-[10px] font-mono tracking-widest text-[#8E9196]">{k}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={!!preset.auto_rotate}
                 onChange={(e) => set({ auto_rotate: e.target.checked })}
                 className="accent-[#00F0FF]"
                 data-testid="preset-autorotate" />
          <span className="uppercase text-[10px] font-mono tracking-widest text-[#8E9196]">Auto-rotate globe</span>
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={!!preset.show_cve_ticker}
                 onChange={(e) => set({ show_cve_ticker: e.target.checked })}
                 className="accent-[#00F0FF]"
                 data-testid="preset-cveticker" />
          <span className="uppercase text-[10px] font-mono tracking-widest text-[#8E9196]">CVE ticker</span>
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={!!preset.show_hotspot_panel}
                 onChange={(e) => set({ show_hotspot_panel: e.target.checked })}
                 className="accent-[#00F0FF]"
                 data-testid="preset-hotspot" />
          <span className="uppercase text-[10px] font-mono tracking-widest text-[#8E9196]">Hotspot panel</span>
        </label>
      </div>
    </div>
  );
}

function PresetModal({ token, onCancel, onSave }) {
  const [preset, setPreset] = useState({ ...DEFAULT_PRESET, ...(token.preset || {}) });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={onCancel} data-testid="preset-modal">
      <div className="bg-[#0a0a0a] border border-[#B57BFF]/50 rounded-sm p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] mb-1">Preset · {token.name}</div>
            <div className="font-display font-black text-xl">Wall screen layout</div>
          </div>
          <button onClick={onCancel} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <PresetEditor preset={preset} onChange={setPreset} />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel}
                  className="px-3 py-2 border border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white rounded-sm">
            Cancel
          </button>
          <button onClick={() => onSave(preset)}
                  data-testid="preset-save-btn"
                  className="px-4 py-2 bg-[#B57BFF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white">
            Save preset
          </button>
        </div>
      </div>
    </div>
  );
}
